import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamEvents } from 'app/frontend/base/config/queries'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useGroupedTeamMembers } from '../../TeamRoster/hooks'
import EventOverviewUser from './EventOverviewUser'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import Select from 'app/frontend/base/components/Select'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import {
  EventOverviewStatusByEventValue,
  type UserStatView,
} from '../utils/useUserStat'
import {
  EventOverviewSortBy,
  EventOverviewSortDirection,
  sortEventOverviewRows,
} from '../utils/sortRows'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { participantColor } from '../utils/participantColor'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'
dayjs.extend(RelativeTime)

const EventOverview = () => {
  const eventWidthPx = 75
  const primaryColumnWidthPx = 160
  const summaryColumnWidthPx = 75
  const { team } = useTeam()
  const { t } = useTranslation()
  const { data: events, isLoading: isLoadingEvents } = useTeamEvents(team.id)
  const [sortBy, setSortBy] = useState<EventOverviewSortBy>('role')
  const [sortDirection, setSortDirection] =
    useState<EventOverviewSortDirection>('asc')
  const [statView, _setStatView] = useState<UserStatView>('presence')
  const cardRef = useRef<HTMLDivElement>(null)
  const [eventAreaWidthPx, setEventAreaWidthPx] = useState(0)

  const visibleEvents = useMemo(() => {
    const eventsList = events ?? []
    if (eventsList.length === 0) return eventsList
    if (eventAreaWidthPx <= 0) return []

    const maxVisibleEvents = Math.max(
      1,
      Math.floor(eventAreaWidthPx / eventWidthPx),
    )
    return eventsList.slice(0, maxVisibleEvents)
  }, [eventAreaWidthPx, events])

  const { groupedMembers } = useGroupedTeamMembers()
  const sortOptions = useMemo(
    () => [
      { label: 'name', value: 'name' },
      { label: 'role', value: 'role' },
      { label: 'class', value: 'class' },
      { label: statView, value: 'custom' },
    ],
    [],
  )

  const stickyTop = useStickyTop()

  const baseRows = useMemo(
    () =>
      groupedMembers.map((membersForUser, index) => {
        const main = membersForUser[0]
        return {
          key: main?.id ?? `row-${index}`,
          main,
          membersForUser,
        }
      }),
    [groupedMembers],
  )

  const statusByRowAndEvent = useMemo(() => {
    const rowByCharacterId = new Map<
      number,
      { rowIndex: number; characterPriority: number }
    >()

    baseRows.forEach((row, rowIndex) => {
      row.membersForUser.forEach((membership, characterPriority) => {
        rowByCharacterId.set(membership.characterReference.id, {
          rowIndex,
          characterPriority,
        })
      })
    })

    const matrix = baseRows.map(
      () => new Map<number, EventOverviewStatusByEventValue>(),
    )

    visibleEvents.forEach((event) => {
      event.participantReferences.forEach((participant) => {
        if (participant.characterId === undefined) return

        const rowMatch = rowByCharacterId.get(participant.characterId)
        if (!rowMatch) return

        const rowStatuses = matrix[rowMatch.rowIndex]
        const existing = rowStatuses.get(event.id)
        if (!existing || rowMatch.characterPriority < existing.priority) {
          rowStatuses.set(event.id, {
            status: participant.status,
            priority: rowMatch.characterPriority,
            participantId: participant.id,
          })
        }
      })
    })

    return matrix
  }, [baseRows, visibleEvents])

  const rows = useMemo(
    () =>
      sortEventOverviewRows({
        rows: baseRows.map((row, rowIndex) => ({
          ...row,
          statusByEvent: statusByRowAndEvent[rowIndex],
        })),
        visibleEvents,
        statView,
        sortBy,
        sortDirection,
      }),
    [
      baseRows,
      sortBy,
      sortDirection,
      statView,
      statusByRowAndEvent,
      visibleEvents,
    ],
  )

  useLayoutEffect(() => {
    const cardElement = cardRef.current
    if (!cardElement) return

    const updateEventAreaWidth = () => {
      const tableWidth = cardElement.getBoundingClientRect().width
      const primaryColumnWidth = primaryColumnWidthPx
      const summaryColumnWidth = summaryColumnWidthPx
      const nextEventAreaWidth = Math.max(
        0,
        tableWidth - primaryColumnWidth - summaryColumnWidth,
      )
      setEventAreaWidthPx(Math.round(nextEventAreaWidth))
    }

    updateEventAreaWidth()

    const resizeObserver = new ResizeObserver(updateEventAreaWidth)
    resizeObserver.observe(cardElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [primaryColumnWidthPx, summaryColumnWidthPx])

  return (
    <Card ref={cardRef} className='pt-0 flex'>
      {!events?.length && !isLoadingEvents ? (
        <div className='flex justify-center items-center my-16'>
          <p className='text-muted-foreground'>
            No upcoming events found. Set up a schedule or create an event to
            get started.
          </p>
        </div>
      ) : (
        <table
          className={cn(
            'border-separate border-spacing-y-1 relative transition-opacity',
            visibleEvents.length === 0 && 'opacity-0 pointer-events-none',
          )}
        >
          <colgroup>
            <col
              style={{
                width: primaryColumnWidthPx,
                maxWidth: primaryColumnWidthPx,
              }}
            />
            <col
              style={{
                width: summaryColumnWidthPx,
                maxWidth: summaryColumnWidthPx,
              }}
            />
            {visibleEvents.map((event) => (
              <col
                key={event.id}
                style={{ width: eventWidthPx, maxWidth: eventWidthPx }}
              />
            ))}
          </colgroup>
          <thead className='[&_th]:align-bottom'>
            <tr>
              <th
                className={cn(
                  'sticky z-30 bg-card border-b font-normal text-xs',
                  stickyTop,
                )}
              >
                <div className='flex mb-1 gap-2 items-center'>
                  <div>
                    {sortDirection === 'asc' ? (
                      <ArrowUpIcon
                        className='w-4 h-4 cursor-pointer'
                        onClick={() => setSortDirection('desc')}
                      />
                    ) : (
                      <ArrowDownIcon
                        className='w-4 h-4 cursor-pointer'
                        onClick={() => setSortDirection('asc')}
                      />
                    )}
                  </div>

                  <div className='flex items-center gap-1'>
                    <span className='text-muted-foreground'>sort by</span>
                    <Select
                      value={sortBy}
                      setValue={(value) => {
                        if (!value) return
                        setSortBy(value as 'name' | 'role' | 'class' | 'custom')
                      }}
                      options={sortOptions}
                      hideIcon
                      className='bg-transparent hover:bg-transparent border-0 w-fit font-normal text-xs p-0 h-5'
                      placeholder='Sort by'
                      optionWidth='fit'
                    />
                  </div>
                </div>
              </th>
              <th className={cn('sticky z-30 bg-card border-b', stickyTop)} />

              {visibleEvents.map((event) => {
                const eventDate = dayjs(event.date)
                const now = dayjs()
                const shouldShowYear =
                  (eventDate.isAfter(now.add(6, 'month')) ||
                    eventDate.isBefore(now.subtract(6, 'month'))) &&
                  eventDate.year() !== now.year()

                return (
                  <th
                    key={event.id}
                    className={cn('sticky z-30 bg-card border-b', stickyTop)}
                  >
                    <Tooltip delayDuration={300} disableHoverableContent>
                      <TooltipTrigger asChild>
                        <Link
                          to={`${team.path}/events/${event.id}`}
                          className='flex flex-col items-center px-1 pb-1 pt-2 group'
                        >
                          <span
                            className={cn(
                              'font-medium whitespace-nowrap leading-tight transition-colors',
                              event.status === 'cancelled'
                                ? 'group-hover:text-destructive-foreground'
                                : 'group-hover:text-primary',
                            )}
                          >
                            {eventDate.format('MMM D')}
                          </span>

                          <span className='text-xs text-muted-foreground whitespace-nowrap font-normal'>
                            {shouldShowYear
                              ? eventDate.format('YYYY')
                              : eventDate.format('ddd')}
                          </span>

                          <div className='relative'>
                            <EventAvatar
                              eventReference={event}
                              variant='square'
                              className={cn(
                                'rounded-[8px] border h-9 w-16 min-w-16 my-1 group-hover:border-primary transition-colors',
                                event.status === 'cancelled' &&
                                  'grayscale opacity-80',
                              )}
                            />

                            {event.optional ? (
                              <span className='absolute top-[6px] right-[4px] bg-info border-info-foreground text-info-foreground flex items-center justify-center px-1 py-0.5 h-4 border rounded-[6px] text-xs font-normal'>
                                Optional
                              </span>
                            ) : null}

                            <span
                              className={cn(
                                'absolute right-[6px] bottom-[5px] capitalize flex items-center justify-center px-1 py-0.5 bg-card-nested h-4 border rounded-t-[6px] text-xs font-normal border-b-transparent transition-colors max-w-[56px]',
                              )}
                            >
                              <span className='truncate'>
                                {event.activityType === 'raids'
                                  ? event.primaryDifficulty?.[0]
                                  : event.activityType === 'custom'
                                  ? event.title
                                  : t(
                                      `dashboard.team.events.activity-types.${event.activityType}`,
                                    )}
                              </span>
                            </span>
                          </div>

                          {event.status === 'cancelled' ? (
                            <span className='font-normal text-sm text-destructive-foreground'>
                              cancelled
                            </span>
                          ) : (
                            <span className='flex items-center gap-1 font-normal text-muted-foreground text-xs'>
                              <span
                                className={cn(
                                  'font-semibold text-sm',
                                  participantColor(event),
                                )}
                              >
                                {
                                  event.participantReferences.filter((p) =>
                                    presentParticipantStatuses.includes(
                                      p.status,
                                    ),
                                  ).length
                                }
                              </span>
                              <span>/</span>
                              <span>{event.participantReferences.length}</span>
                            </span>
                          )}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent
                        sideOffset={-6}
                        className='flex flex-col items-center'
                      >
                        <div>
                          {`${event.title}${
                            event.activityType === 'raids' &&
                            event.primaryDifficulty
                              ? ` (${
                                  difficultiesLabels[event.primaryDifficulty]
                                })`
                              : ''
                          }`}
                        </div>

                        <span className='text-xs text-muted-foreground'>
                          {event.startTime} - {event.finishTime}
                        </span>

                        <span className='text-xs text-foreground'>
                          {dayjs(event.startsAt).fromNow()}
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              return (
                <EventOverviewUser
                  key={row.key}
                  main={row.main}
                  members={row.membersForUser}
                  visibleEvents={visibleEvents}
                  statusByEvent={row.statusByEvent}
                  statView={statView}
                />
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}

export default EventOverview
